import { useTranslation } from "react-i18next";
import {
  promotionTypes,
  PromotionTypeValue,
  promotionStatuses,
  PromotionStatusValue,
  questStatuses,
  QuestStatusValue,
  questCreateStatuses,
  QuestCreateStatusValue,
  ownerTypes,
  OwnerTypeValue,
  auditLogOperations,
  AuditLogOperationValue,
  genders,
  GenderValue,
  maritalStatuses,
  MaritalStatusValue,
  joinConditions,
  JoinConditionValue,
  profileTypes,
  ProfileTypeValue,
  templateScopes,
  TemplateScopeValue,
  linkParams,
  LinkParamValue,
} from "@quest_editor/constants";

export type ConstOption<T> = {
  value: T;
  label: string;
};

const getConstOptions = <T extends string>(
  values: readonly T[],
  i18nKey: string,
): ConstOption<T>[] => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation("const");
  return values.map((value) => ({
    value,
    label: t(`${i18nKey}.${value}`),
  }));
};

export const getPromotionTypeOptions = (): ConstOption<PromotionTypeValue>[] => {
  return getConstOptions(promotionTypes, "promotion_type");
};

export const getPromotionStatusOptions =
  (): ConstOption<PromotionStatusValue>[] => {
    return getConstOptions(promotionStatuses, "promotion_status");
  };

export const getQuestStatusOptions = (): ConstOption<QuestStatusValue>[] => {
  return getConstOptions(questStatuses, "quest_status");
};

export const getQuestCreateStatusOptions =
  (): ConstOption<QuestCreateStatusValue>[] => {
    return getConstOptions(questCreateStatuses, "quest_create_status");
  };

export const getOwnerTypeOptions = (): ConstOption<OwnerTypeValue>[] => {
  return getConstOptions(ownerTypes, "owner_type");
};

export const getAuditLogOperationOptions =
  (): ConstOption<AuditLogOperationValue>[] => {
    return getConstOptions(auditLogOperations, "audit_log_operation");
  };

export const getGenderOptions = (): ConstOption<GenderValue>[] => {
  return getConstOptions(genders, "gender");
};

export const getMaritalStatusOptions = (): ConstOption<MaritalStatusValue>[] => {
  return getConstOptions(maritalStatuses, "marital_status");
};

export const getJoinConditionOptions = (): ConstOption<JoinConditionValue>[] => {
  return getConstOptions(joinConditions, "join_condition");
};

export const getProfileTypeOptions = (): ConstOption<ProfileTypeValue>[] => {
  return getConstOptions(profileTypes, "profile_type");
};

export const getTemplateScopeOptions = (): ConstOption<TemplateScopeValue>[] => {
  return getConstOptions(templateScopes, "template_scope");
};

export const getLinkParamOptions = (): ConstOption<LinkParamValue>[] => {
  return getConstOptions(linkParams, "link_param");
};

export const getConstLabel = (i18nKey: string, value: string): string => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { t } = useTranslation("const");
  return t(`${i18nKey}.${value}`);
};
